import { useMutation, type UseMutationResult, useQuery, useQueryClient, type UseQueryResult } from 'react-query'

import { billingEngineRepository } from '@/src/api/BillingEngineRepository'
import type {
  AutoReloadResponse,
  AutoReloadSettings,
  CreateWalletCheckoutParams,
  CreateWalletCheckoutResponse,
  CreditPurchaseOptionsResponse,
  UpdateAutoReloadParams,
  WalletCheckoutStatusResponse,
  WalletStatistics,
  WalletTransaction,
  WalletTransactionsResponse,
} from '@/src/types/billingEngine'

/**
 * Fetch wallet balance and statistics
 */
export function useBillingEngineWallet(): UseQueryResult<WalletStatistics, Error> {
  return useQuery(
    ['billing-engine-wallet'],
    async () => {
      const response = await billingEngineRepository.getWallet()
      return response.data.wallet
    },
    {
      staleTime: 30 * 1000, // 30 seconds
      cacheTime: 5 * 60 * 1000, // 5 minutes
      refetchOnWindowFocus: true,
    },
  )
}

/**
 * Fetch wallet transaction history
 */
export function useBillingEngineWalletTransactions(limit?: number): UseQueryResult<WalletTransaction[], Error> {
  return useQuery(
    ['billing-engine-wallet-transactions', limit],
    async () => {
      const response = await billingEngineRepository.getWalletTransactions(limit)
      return response.data.transactions
    },
    {
      staleTime: 1 * 60 * 1000, // 1 minute
      cacheTime: 5 * 60 * 1000, // 5 minutes
    },
  )
}

/**
 * Fetch paginated wallet transaction history
 */
export function useBillingEngineWalletTransactionsPage(page: number, perPage: number): UseQueryResult<WalletTransactionsResponse, Error> {
  return useQuery(
    ['billing-engine-wallet-transactions', 'page', page, perPage],
    async () => {
      const response = await billingEngineRepository.getWalletTransactions({ page, per_page: perPage })
      return response.data
    },
    {
      keepPreviousData: true,
      staleTime: 1 * 60 * 1000, // 1 minute
      cacheTime: 5 * 60 * 1000, // 5 minutes
    },
  )
}

/**
 * Fetch credit purchase options
 */
export function useCreditPurchaseOptions(): UseQueryResult<CreditPurchaseOptionsResponse, Error> {
  return useQuery(
    ['billing-engine-credit-purchase-options'],
    async () => {
      const response = await billingEngineRepository.getCreditPurchaseOptions()
      return response.data
    },
    {
      staleTime: 5 * 60 * 1000, // 5 minutes
      cacheTime: 10 * 60 * 1000, // 10 minutes
    },
  )
}

/**
 * Create wallet checkout session
 */
export function useCreateWalletCheckout(): UseMutationResult<CreateWalletCheckoutResponse, Error, CreateWalletCheckoutParams> {
  const queryClient = useQueryClient()

  return useMutation(
    async (params: CreateWalletCheckoutParams) => {
      const response = await billingEngineRepository.createWalletCheckout(params)
      return response.data
    },
    {
      onSuccess: () => {
        // Invalidate wallet query to refetch after checkout
        queryClient.invalidateQueries(['billing-engine-wallet'])
      },
    },
  )
}

/**
 * Poll wallet checkout status
 */
export function useWalletCheckoutStatus(creditPurchaseId: number | null): UseQueryResult<WalletCheckoutStatusResponse, Error> {
  const queryClient = useQueryClient()

  return useQuery(
    ['billing-engine-wallet-checkout-status', creditPurchaseId],
    async () => {
      if (!creditPurchaseId) throw new Error('Credit purchase ID required')
      const response = await billingEngineRepository.getWalletCheckoutStatus(creditPurchaseId)
      return response.data
    },
    {
      enabled: Boolean(creditPurchaseId),
      refetchInterval: (data) => {
        // Stop polling if completed or failed
        if (data?.status === 'completed' || data?.status === 'failed') {
          // Invalidate wallet query to refresh balance
          queryClient.invalidateQueries(['billing-engine-wallet'])
          queryClient.invalidateQueries(['billing-engine-wallet-transactions'])
          queryClient.invalidateQueries(['billing-engine-purchases'])
          queryClient.invalidateQueries(['billing-engine-profile'])
          return false
        }
        return 5000 // Poll every 5 seconds while pending
      },
      staleTime: 0,
    },
  )
}

/**
 * Fetch auto-reload settings
 */
export function useAutoReloadSettings(): UseQueryResult<AutoReloadResponse, Error> {
  return useQuery(
    ['billing-engine-auto-reload-settings'],
    async () => {
      const response = await billingEngineRepository.getAutoReloadSettings()
      return response.data
    },
    {
      staleTime: 30 * 1000, // 30 seconds
      cacheTime: 5 * 60 * 1000, // 5 minutes
      refetchOnWindowFocus: true,
    },
  )
}

/**
 * Update auto-reload settings
 */
export function useUpdateAutoReloadSettings(): UseMutationResult<AutoReloadSettings, Error, UpdateAutoReloadParams> {
  const queryClient = useQueryClient()

  return useMutation(
    async (params: UpdateAutoReloadParams) => {
      const response = await billingEngineRepository.updateAutoReloadSettings(params)
      return response.data.auto_reload
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['billing-engine-auto-reload-settings'])
      },
    },
  )
}
